(function () {
  'use strict';

  if (window.__tuyaReviewsModalLoaded) return;
  window.__tuyaReviewsModalLoaded = true;

  const MODAL_ID = 'tuya-reviews-modal-shared';
  const SECTION_ID = 'tuya-reviews-modal-content';
  const CLEANUP_DELAYS = [100, 300, 800, 2000];
  const STYLE_DELAYS = [200, 1000, 2500];

  let currentProductId = null;
  let lastTrigger = null;
  const fetchCache = {};

  /* ── Modal DOM ─────────────────────────────────────────────────────────── */

  function setupModalEvents(overlay) {
    setupHistogramFiltering(overlay);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) closeModal(); });
    overlay.querySelector('[data-modal-close]').addEventListener('click', closeModal);
    overlay.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeModal(); });
  }

  function getOrCreateModal() {
    const existing = document.getElementById(MODAL_ID);
    if (existing) return existing;

    const tpl = document.querySelector('.tuya-reviews-modal-tpl');
    if (!tpl) {
      console.warn('[Tuya Reviews Modal] Template element not found. Ensure tuya-reviews-modal-overlay snippet is rendered.');
      return null;
    }

    const overlay = tpl.content.firstElementChild.cloneNode(true);
    document.body.appendChild(overlay);
    setupModalEvents(overlay);
    return overlay;
  }

  /* ── Section Rendering API fetch ───────────────────────────────────────── */

  function fetchReviewsSection(productUrl, productId) {
    if (fetchCache[productId]) return Promise.resolve(fetchCache[productId]);

    const sep = productUrl.includes('?') ? '&' : '?';
    const url = `${productUrl}${sep}sections=${SECTION_ID}`;

    return fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Section fetch failed: ${res.status}`);
        return res.json();
      })
      .then((json) => {
        const html = json[SECTION_ID];
        if (!html) throw new Error('Section not found in response');
        fetchCache[productId] = html;
        return html;
      });
  }

  /* ── Populate modal ─────────────────────────────────────────────────────── */

  function populateModal(trigger) {
    const modal = getOrCreateModal();
    const productId = trigger.getAttribute('data-product-id');
    const productTitle = trigger.getAttribute('data-product-title');
    const productUrl = trigger.getAttribute('data-product-url');

    // Read i18n texts from trigger data attributes (set by Liquid snippet)
    const i18n = {
      close: trigger.getAttribute('data-i18n-close'),
      viewProduct: trigger.getAttribute('data-i18n-view-product'),
      error: trigger.getAttribute('data-i18n-error'),
      labelOne: trigger.getAttribute('data-i18n-label-one'),
      labelFew: trigger.getAttribute('data-i18n-label-few'),
      labelOther: trigger.getAttribute('data-i18n-label-other'),
    };

    // Update close button label
    const closeBtn = modal.querySelector('[data-modal-close]');
    if (closeBtn) closeBtn.setAttribute('aria-label', i18n.close);

    // Heading
    const headingEl = modal.querySelector('[data-modal-heading]');
    headingEl.textContent = productTitle;
    modal.setAttribute('aria-label', productTitle);

    // Subheading with rating summary
    const ratingValue = parseFloat(trigger.getAttribute('data-rating-value')) || 0;
    const ratingCount = parseInt(trigger.getAttribute('data-rating-count'), 10) || 0;
    const subheadingEl = modal.querySelector('[data-modal-subheading]');

    if (subheadingEl) {
      if (ratingCount > 0) {
        const stars = Array.from({ length: 5 }, (_, i) => i < Math.round(ratingValue) ? '★' : '☆').join('');
        subheadingEl.innerHTML =
          `<span class="tuya-reviews-modal__rating-val">${ratingValue.toFixed(1)}</span> ` +
          `<span class="tuya-reviews-modal__rating-stars" aria-hidden="true">${stars}</span> ` +
          `<span class="tuya-reviews-modal__rating-count">(${ratingCount} ${pluralizeOpinia(ratingCount, i18n)})</span>`;
        subheadingEl.style.display = 'flex';
      } else {
        subheadingEl.style.display = 'none';
      }
    }

    // Footer link — hide when on any product page
    const linkEl = modal.querySelector('[data-modal-product-link]');
    const footerEl = modal.querySelector('.tuya-reviews-modal__footer');
    const isOnProductPage = window.location.pathname.includes('/products/');

    if (isOnProductPage) {
      if (linkEl) linkEl.style.display = 'none';
      if (footerEl) footerEl.style.display = 'none';
    } else {
      if (linkEl) {
        linkEl.href = productUrl;
        linkEl.textContent = i18n.viewProduct;
        linkEl.style.display = 'flex';
      }
      if (footerEl) footerEl.style.display = 'block';
    }

    if (currentProductId === productId) return;
    currentProductId = productId;

    const widgetWrapper = modal.querySelector('[data-modal-widget]');
    const loaderTpl = document.querySelector('.tuya-reviews-modal-loader-tpl');
    widgetWrapper.replaceChildren(
      loaderTpl ? loaderTpl.content.firstElementChild.cloneNode(true) : document.createElement('div')
    );


    fetchReviewsSection(productUrl, productId)
      .then((html) => {
        if (currentProductId !== productId) return;

        widgetWrapper.innerHTML = html;
        executeScripts(widgetWrapper);
        cleanUpWidget(widgetWrapper, i18n);
        reinitJudgeme();

        CLEANUP_DELAYS.forEach((delay) => setTimeout(() => cleanUpWidget(widgetWrapper, i18n), delay));
        STYLE_DELAYS.forEach((delay) => setTimeout(reinsertTuyaStyles, delay));
      })
      .catch((err) => {
        if (currentProductId !== productId) return;
        console.warn('[Tuya Reviews Modal] Failed to fetch reviews:', err);
        const errorEl = document.createElement('p');
        errorEl.style.cssText = 'text-align:center;color:var(--tuya-color-text-muted);padding:var(--tuya-space-8) 0;';
        errorEl.textContent = i18n.error;
        widgetWrapper.replaceChildren(errorEl);
      });
  }

  /* ── Script execution helper ────────────────────────────────────────────── */

  const ALLOWED_SCRIPT_HOSTS = ['judge.me', 'cdn.judge.me'];

  function executeScripts(container) {
    container.querySelectorAll('script').forEach((orig) => {
      const next = document.createElement('script');
      if (orig.src) {
        try {
          const url = new URL(orig.src);
          if (!ALLOWED_SCRIPT_HOSTS.some((h) => url.hostname === h || url.hostname.endsWith('.' + h))) return;
        } catch { return; }
        if (!document.querySelector(`script[src="${CSS.escape(orig.src)}"]`)) {
          next.src = orig.src;
          next.defer = true;
          document.head.appendChild(next);
        }
      } else {
        next.textContent = orig.textContent;
        document.head.appendChild(next);
        next.remove();
      }
      orig.remove();
    });
  }

  /* ── Widget cleanup ─────────────────────────────────────────────────────── */

  function cleanUpWidget(wrapper, i18n = {}) {
    if (!wrapper) return;

    wrapper.querySelectorAll('.jdgm-temp-hiding-style').forEach((el) => el.remove());

    wrapper.querySelectorAll(
      '.jdgm-widget, .jdgm-review-widget, .jm-review-widget, .jdgm-legacy-widget-content,' +
      '.jdgm-rev-widg, .jdgm-rev-widg__header, .jdgm-rev-widg__body,' +
      '.jdgm-rev-widg__reviews, .jdgm-rev, .jdgm-rev__header, .jdgm-rev__content, .jdgm-histogram'
    ).forEach((el) => { el.style.display = 'block'; });

    wrapper.querySelectorAll('.jdgm-temp-hidden')
      .forEach((el) => el.classList.remove('jdgm-temp-hidden'));

    const currentLocale = (window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'pl';

    wrapper.querySelectorAll('.jdgm-rev__timestamp').forEach((el) => {
      const raw = el.getAttribute('data-content');
      if (raw) {
        try {
          el.textContent = new Date(raw).toLocaleDateString(currentLocale, {
            year: 'numeric', month: 'long', day: 'numeric',
          });
        } catch {
          el.textContent = raw.split(' ')[0];
        }
      }
      el.classList.remove('jdgm-spinner');
    });



    wrapper.querySelectorAll('.jdgm-rev-widg__title').forEach((el) => { el.style.display = 'none'; });

    wrapper.querySelectorAll(
      '.jdgm-write-rev-link, .jdgm-rev-widg__sort-wrapper,' +
      '.jdgm-paginate, .jdgm-rev-widg__paginate-spinner-wrapper'
    ).forEach((el) => { el.style.display = 'none'; });
  }

  /* ── Judge.me re-initialization ─────────────────────────────────────────── */

  function reinitJudgeme() {
    function tryInit() {
      try {
        if (!window.jdgm) return;
        if (typeof jdgm.initWidgets === 'function') jdgm.initWidgets();
        if (typeof jdgm.generateWidgets === 'function') jdgm.generateWidgets();
        if (jdgm.customWidgets && typeof jdgm.customWidgets.reinitWidgets === 'function') {
          jdgm.customWidgets.reinitWidgets();
        }
      } catch { /* silent */ }
    }

    tryInit();
    try { document.dispatchEvent(new CustomEvent('jdgm:loaded')); } catch { /* silent */ }
    [300, 800, 2000].forEach((delay) => setTimeout(tryInit, delay));
  }

  /* ── CSS specificity fix (one-shot) ───────────────────────────────────────── */

  let stylesReinserted = false;
  function reinsertTuyaStyles() {
    if (stylesReinserted) return;
    stylesReinserted = true;
    ['link[href*="tuya-reviews-modal.css"]', 'link[href*="tuya-judgeme-reviews.css"]'].forEach((sel) => {
      const link = document.querySelector(sel);
      if (!link?.parentNode) return;
      const clone = link.cloneNode(true);
      link.remove();
      document.head.appendChild(clone);
    });
  }

  /* ── Histogram filtering ─────────────────────────────────────────────────── */

  function setupHistogramFiltering(modal) {
    const widgetWrapper = modal.querySelector('[data-modal-widget]');
    if (!widgetWrapper) return;

    widgetWrapper.addEventListener('click', (e) => {
      const row = e.target.closest('.jdgm-histogram__row, [class*="histogram__row"]');
      if (!row) return;

      e.preventDefault();
      e.stopPropagation();

      const score = row.getAttribute('data-rating') || row.getAttribute('data-score');
      if (!score) return;

      const allRows = widgetWrapper.querySelectorAll('.jdgm-histogram__row, [class*="histogram__row"]');
      const allReviews = widgetWrapper.querySelectorAll('.jdgm-rev, .jdgm-review-card');
      const isActive = row.classList.contains('tuya-row-active');

      allRows.forEach((r) => {
        r.classList.remove('tuya-row-active');
        r.style.opacity = isActive ? '1' : '0.5';
      });

      if (isActive) {
        allReviews.forEach((rev) => rev.style.setProperty('display', 'flex', 'important'));
      } else {
        row.classList.add('tuya-row-active');
        row.style.opacity = '1';
        allReviews.forEach((rev) => {
          const ratingEl = rev.querySelector('.jdgm-rev__rating, [data-score]');
          const revScore = ratingEl?.getAttribute('data-score') ?? null;
          rev.style.setProperty('display', revScore === score ? 'flex' : 'none', 'important');
        });
      }
    });
  }

  /* ── Open / Close ────────────────────────────────────────────────────────── */

  function openModal(trigger) {
    lastTrigger = trigger;
    const modal = getOrCreateModal();
    populateModal(trigger);
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    document.querySelectorAll('#MainContent, #header-group, footer').forEach((el) => {
      el.setAttribute('inert', '');
    });

    const closeBtn = modal.querySelector('.tuya-reviews-modal__close');
    if (closeBtn) setTimeout(() => closeBtn.focus(), 100);
  }

  function closeModal() {
    const modal = document.getElementById(MODAL_ID);
    if (!modal) return;
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    document.querySelectorAll('#MainContent, #header-group, footer').forEach((el) => {
      el.removeAttribute('inert');
    });

    if (lastTrigger) { lastTrigger.focus(); lastTrigger = null; }
  }

  /* ── Trigger binding ─────────────────────────────────────────────────────── */

  function bindTriggers() {
    document.querySelectorAll('[data-tuya-reviews-trigger]').forEach((trigger) => {
      if (trigger.dataset.tuyaReviewsBound === 'true') return;
      trigger.dataset.tuyaReviewsBound = 'true';

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        openModal(trigger);
      });

      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          openModal(trigger);
        }
      });
    });
  }

  /* ── Helpers ─────────────────────────────────────────────────────────────── */

  function pluralizeOpinia(n, i18n = {}) {
    if (n === 1) return i18n.labelOne;
    const last2 = n % 100;
    const last1 = n % 10;
    if (last2 >= 12 && last2 <= 14) return i18n.labelOther;
    if (last1 >= 2 && last1 <= 4) return i18n.labelFew;
    return i18n.labelOther;
  }

  /* ── Init ────────────────────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindTriggers);
  } else {
    bindTriggers();
  }

  document.addEventListener('shopify:section:load', () => setTimeout(bindTriggers, 300));

  let bindTriggersTimer;
  const debouncedBindTriggers = () => {
    clearTimeout(bindTriggersTimer);
    bindTriggersTimer = setTimeout(bindTriggers, 200);
  };
  const bodyObserver = new MutationObserver(debouncedBindTriggers);
  if (document.body) {
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      bodyObserver.observe(document.body, { childList: true, subtree: true });
    });
  }
})();
